import { CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { longDate } from "../format";

const MONTHS = Array.from({ length: 12 }, (_, i) => new Intl.DateTimeFormat("pl-PL", { month: "long", timeZone: "UTC" }).format(new Date(Date.UTC(2024, i, 1))));
const WEEKDAYS = ["pn", "wt", "śr", "cz", "pt", "so", "nd"]; // weeks start on Monday
const pad = (n: number) => String(n).padStart(2, "0");
const iso = (y: number, m: number, d: number) => `${y}-${pad(m + 1)}-${pad(d)}`; // m: 0-11
const parts = (s: string) => ({ y: Number(s.slice(0, 4)), m: Number(s.slice(5, 7)) - 1, d: Number(s.slice(8, 10)) });
const daysIn = (y: number, m: number) => new Date(Date.UTC(y, m + 1, 0)).getUTCDate();
const mondayIndex = (y: number, m: number) => (new Date(Date.UTC(y, m, 1)).getUTCDay() + 6) % 7;

/** A calendar that shows which days have a chart. Days without one cannot be picked.
 *  `dates` are the available days (ISO), `value` the selected one. */
export function DatePicker({ value, dates, onChange, label, align = "center" }: {
  value: string;
  dates: string[] | undefined;
  onChange: (iso: string) => void;
  label: string;
  align?: "center" | "start";
}) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const grid = useRef<HTMLDivElement>(null);
  const id = useId();
  const wantFocus = useRef(false); // move focus into the grid only after opening or a keyboard move, never while a select is used
  const available = useMemo(() => new Set(dates ?? []), [dates]);
  const first = dates?.[0], last = dates?.[dates.length - 1];
  const start = parts(value || last || "2000-01-01");
  const [view, setView] = useState({ y: start.y, m: start.m });
  const [focus, setFocus] = useState(value || last || "");

  const openIt = () => {
    const base = parts(value || last || "2000-01-01");
    setView({ y: base.y, m: base.m });
    setFocus(value || last || "");
    wantFocus.current = true;
    setOpen(true);
  };
  const close = (refocus = true) => { setOpen(false); if (refocus) trigger.current?.focus(); };

  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => { if (!root.current?.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", away);
    return () => document.removeEventListener("mousedown", away);
  }, [open]);
  useEffect(() => {
    if (!open || !wantFocus.current) return;
    wantFocus.current = false;
    grid.current?.querySelector<HTMLElement>('[tabindex="0"]')?.focus();
  }, [open, focus, view]);

  const minYear = first ? parts(first).y : 1990, maxYear = last ? parts(last).y : new Date().getFullYear();
  const years = Array.from({ length: maxYear - minYear + 1 }, (_, i) => maxYear - i);
  const goMonth = (delta: number) => setView(({ y, m }) => { const t = y * 12 + m + delta; return { y: Math.floor(t / 12), m: t % 12 }; });
  const monthHasChart = (y: number, m: number) => dates?.some((d) => d.startsWith(`${y}-${pad(m + 1)}`)) ?? false;

  const shiftMonth = (delta: number) => {
    const t = view.y * 12 + view.m + delta;
    const y = Math.floor(t / 12), m = ((t % 12) + 12) % 12;
    wantFocus.current = true;
    setView({ y, m });
    setFocus(iso(y, m, 1));
  };
  const move = (delta: number) => {
    wantFocus.current = true;
    const p = parts(focus || iso(view.y, view.m, 1));
    const next = new Date(Date.UTC(p.y, p.m, p.d + delta));
    const y = next.getUTCFullYear(), m = next.getUTCMonth();
    setView({ y, m });
    setFocus(iso(y, m, next.getUTCDate()));
  };

  const onKey = (e: React.KeyboardEvent) => {
    const step: Record<string, number> = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 };
    if (e.key in step) { e.preventDefault(); move(step[e.key]!); }
    else if (e.key === "PageUp" || e.key === "PageDown") { e.preventDefault(); shiftMonth(e.key === "PageUp" ? -1 : 1); }
    else if (e.key === "Escape") { e.preventDefault(); close(); }
  };

  const cells: (number | null)[] = [...Array(mondayIndex(view.y, view.m)).fill(null), ...Array.from({ length: daysIn(view.y, view.m) }, (_, i) => i + 1)];
  const fp = parts(focus || "0000-01-01");
  const focusIso = fp.y === view.y && fp.m === view.m ? focus : iso(view.y, view.m, 1);

  return (
    <div className={`datepicker datepicker--${align}`} ref={root}>
      <button ref={trigger} type="button" className="datepicker__trigger" aria-haspopup="dialog" aria-expanded={open}
        aria-label={`${label}: ${value ? longDate(value) : "wybierz dzień"}`} onClick={() => (open ? close(false) : openIt())}>
        <CalendarDays size={18} aria-hidden="true" />
        <span>{value ? longDate(value) : "Wybierz dzień"}</span>
      </button>
      {open && (
        <div className="datepicker__pop" role="dialog" aria-label={label} onKeyDown={onKey}>
          <div className="datepicker__head">
            <button type="button" className="datepicker__nav" aria-label="Poprzedni miesiąc" onClick={() => goMonth(-1)}><ChevronLeft size={18} aria-hidden="true" /></button>
            <select aria-label="Miesiąc" value={view.m} onChange={(e) => setView({ ...view, m: Number(e.target.value) })}>
              {MONTHS.map((n, i) => <option key={n} value={i}>{n}{monthHasChart(view.y, i) ? "" : " (brak)"}</option>)}
            </select>
            <select aria-label="Rok" value={view.y} onChange={(e) => setView({ ...view, y: Number(e.target.value) })}>
              {years.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
            <button type="button" className="datepicker__nav" aria-label="Następny miesiąc" onClick={() => goMonth(1)}><ChevronRight size={18} aria-hidden="true" /></button>
          </div>
          <div className="datepicker__grid" role="grid" aria-labelledby={id} ref={grid}>
            <span id={id} className="sr-only">{MONTHS[view.m]} {view.y}</span>
            {WEEKDAYS.map((w) => <span key={w} className="datepicker__dow" role="columnheader">{w}</span>)}
            {cells.map((d, i) => {
              if (d === null) return <span key={`b${i}`} />;
              const day = iso(view.y, view.m, d);
              const has = available.has(day), selected = day === value;
              return (
                <button key={day} type="button" role="gridcell" tabIndex={day === focusIso ? 0 : -1}
                  className={`datepicker__day${has ? " has-chart" : ""}${selected ? " is-selected" : ""}`}
                  aria-selected={selected} aria-disabled={!has} aria-label={`${longDate(day)}${has ? ", jest notowanie" : ", brak notowania"}`}
                  onClick={() => { if (has) { onChange(day); close(); } }}
                  onFocus={() => setFocus(day)}>
                  {d}
                </button>
              );
            })}
          </div>
          <div className="datepicker__foot">
            <span><i className="datepicker__key" /> jest notowanie</span>
            {last && <button type="button" onClick={() => { onChange(last); close(); }}>Najnowsze</button>}
            {first && <button type="button" onClick={() => { onChange(first); close(); }}>Najstarsze</button>}
          </div>
        </div>
      )}
    </div>
  );
}
